export type ReproBreadcrumbWindow = {
  maxEvents: number;
  maxAgeMs: number;
};

export type ReproBreadcrumbBase = {
  at: string; // ISO
};

export type ReproRouteBreadcrumb = ReproBreadcrumbBase & {
  t: 'route';
  path: string;
};

export type ReproUiBreadcrumb = ReproBreadcrumbBase & {
  t: 'ui';
  label: string;
  selector?: string;
  href?: string;
  role?: string;
};

export type ReproApiBreadcrumb = ReproBreadcrumbBase & {
  t: 'api';
  method: string;
  path: string;
  status?: number;
  durationMs?: number;
  code?: string;
  req?: unknown;
  res?: unknown;
};

export type ReproBreadcrumb = ReproRouteBreadcrumb | ReproUiBreadcrumb | ReproApiBreadcrumb;

const DEFAULT_WINDOW: ReproBreadcrumbWindow = { maxEvents: 25, maxAgeMs: 120_000 };

let windowConfig: ReproBreadcrumbWindow = DEFAULT_WINDOW;
let events: ReproBreadcrumb[] = [];

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;
export type ReproBreadcrumbInput = DistributiveOmit<ReproBreadcrumb, 'at'> & { at?: string };

export function configureReproBreadcrumbWindow(next?: Partial<ReproBreadcrumbWindow> | null): void {
  if (!next) return;
  const maxEvents = Number(next.maxEvents);
  const maxAgeMs = Number(next.maxAgeMs);
  if (Number.isFinite(maxEvents) && maxEvents > 0) windowConfig.maxEvents = Math.floor(maxEvents);
  if (Number.isFinite(maxAgeMs) && maxAgeMs > 0) windowConfig.maxAgeMs = Math.floor(maxAgeMs);
  pruneReproBreadcrumbs();
}

export function clearReproBreadcrumbs(): void {
  events = [];
}

export function recordReproBreadcrumb(input: ReproBreadcrumbInput): void {
  const at = typeof input.at === 'string' && input.at.trim() ? input.at.trim() : new Date().toISOString();
  events.push({ ...(input as ReproBreadcrumb), at });
  pruneReproBreadcrumbs();
}

export function readReproBreadcrumbs(): { window: ReproBreadcrumbWindow; events: ReproBreadcrumb[] } {
  pruneReproBreadcrumbs();
  return { window: { ...windowConfig }, events: [...events] };
}

function pruneReproBreadcrumbs(): void {
  const max = windowConfig.maxEvents;
  const ageMs = windowConfig.maxAgeMs;
  const now = Date.now();
  events = events.filter((e) => {
    const t = Date.parse(e.at);
    if (!Number.isFinite(t)) return true;
    return now - t <= ageMs;
  });
  if (events.length > max) events = events.slice(events.length - max);
}

const SENSITIVE_KEY_RE = /(pass(word)?|token|secret|cookie|auth(orization)?|otp|session)/i;

export function sanitizeReproValue(value: unknown, opts?: { maxString?: number; maxArray?: number }): unknown {
  const maxString = Math.max(20, Math.min(opts?.maxString ?? 300, 5000));
  const maxArray = Math.max(5, Math.min(opts?.maxArray ?? 20, 200));

  const walk = (v: unknown, depth: number): unknown => {
    if (depth > 6) return '[truncated]';
    if (v == null) return v;
    if (typeof v === 'string') {
      const s = v.trim();
      return s.length > maxString ? `${s.slice(0, maxString)}…` : s;
    }
    if (typeof v === 'number' || typeof v === 'boolean') return v;
    if (typeof v === 'bigint') return String(v);
    if (v instanceof Date) return v.toISOString();
    if (Array.isArray(v)) return v.slice(0, maxArray).map((x) => walk(x, depth + 1));
    if (typeof v === 'object') {
      const o = v as Record<string, unknown>;
      const out: Record<string, unknown> = {};
      for (const [k, vv] of Object.entries(o)) {
        if (SENSITIVE_KEY_RE.test(k)) {
          out[k] = '[redacted]';
          continue;
        }
        out[k] = walk(vv, depth + 1);
      }
      return out;
    }
    return String(v);
  };

  return walk(value, 0);
}

